import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';
import ArrowBackIosNewSharpIcon from '@mui/icons-material/ArrowBackIosNewSharp';
import IconButton from '@mui/material/IconButton';
import { ModalAllder } from 'layouts/ModalAllder';
import { DataGrid } from '@mui/x-data-grid';
import { branchTagHandler } from '../../../../redux/handlers';

export default function TagModal({
  modal,
  setModal,
  businessSlug,
  branchId,
  onSuccess,
  mode = 'create', // 'create' or 'edit'
  initialData = null
}) {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const discountTypeOptions = [
    { value: 'percent', label: 'เปอร์เซ็นต์' },
    { value: 'amount', label: 'บาท' }
  ];

  const schema = yup.object().shape({
    name: yup.string().required('ชื่อ TAG จำเป็นต้องระบุ'),
    discount_value: yup.number().required('จำนวนจำเป็นต้องระบุ').positive('จำนวนต้องเป็นตัวเลขบวก'),
    discount_type: yup
      .string()
      .required('ประเภทส่วนลดจำเป็นต้องเลือก')
      .oneOf(['percent', 'amount'], 'ประเภทส่วนลดไม่ถูกต้อง')
  });

  // Initialize form data based on mode
  const getInitialValues = () => {
    if (mode === 'edit' && initialData) {
      return {
        name: initialData.name || initialData.tagName || '',
        discount_value: initialData.discount_value || initialData.quantity || '',
        discount_type: initialData.discount_type || ''
      };
    }
    return {
      name: '',
      discount_value: '',
      discount_type: ''
    };
  };

  const [tagdata, setTagdata] = useState(getInitialValues());

  // Reset form data when modal opens/closes or mode changes
  useEffect(() => {
    if (modal.open) {
      setTagdata(getInitialValues());
      setIsSubmitting(false);
    }
  }, [modal.open, mode, initialData]);

  // Reset form when modal closes
  useEffect(() => {
    if (!modal.open) {
      setTagdata({
        name: '',
        discount_value: '',
        discount_type: ''
      });
      setIsSubmitting(false);
    }
  }, [modal.open]);

  const handleDiscountValueChange = (e, setFieldValue, discount_type) => {
    let value = e.target.value;
    if (value === '') {
      setFieldValue('discount_value', value);
      return;
    }
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      setFieldValue('discount_value', value);
      return;
    }
    if (discount_type === 'percent' && numericValue > 100) {
      setFieldValue('discount_value', '100');
    } else {
      setFieldValue('discount_value', value);
    }
  };

  const handleSubmit = async values => {
    try {
      console.log('values : ', values);
      setIsSubmitting(true);

      const submitData = {
        name: values.name,
        discount_type: values.discount_type,
        discount_value: parseFloat(values.discount_value)
      };

      let result;

      if (mode === 'create') {
        result = await dispatch(branchTagHandler.createBranchTag(businessSlug, branchId, submitData));
      } else if (mode === 'edit' && initialData?.id) {
        result = await dispatch(branchTagHandler.updateBranchTag(businessSlug, initialData.id, submitData));
      }

      if (result) {
        setModal(prev => ({ ...prev, open: false }));
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} tag:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setModal(prev => ({ ...prev, open: false }));
    // Reset form data immediately
    setTagdata({
      name: '',
      discount_value: '',
      discount_type: ''
    });
    setIsSubmitting(false);
  };

  const modalTitle = mode === 'create' ? 'เพิ่มส่วนลด' : 'แก้ไขส่วนลด';

  return (
    <>
      <ModalAllder modal={modal} setModal={setModal} htitle={modalTitle} hbtn={true}>
        <div className="py-4">
          <div className="">
            <Formik
              enableReinitialize
              initialValues={tagdata}
              validationSchema={schema}
              onSubmit={handleSubmit}
              key={`${modal.open}-${mode}-${initialData?.id || 'new'}`} // Force re-render when props change
            >
              {({ handleSubmit, handleChange, touched, errors, values, setFieldValue, isInvalid, resetForm }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <Form.Group className="mb-3 form-st-3 pb-1">
                    <Form.Label className="mb-1">
                      ชื่อ TAG <span className="text-danger">*</span>{' '}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      className="form-control"
                      placeholder="ชื่อ TAG"
                      value={values.name}
                      onChange={handleChange}
                      isInvalid={touched.name && !!errors.name}
                      disabled={isSubmitting}
                    />
                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                  </Form.Group>

                  <div className="row">
                    <div className="col-6">
                      <Form.Group className="mb-3 form-st-3 pb-1">
                        <Form.Label className="mb-1">
                          ประเภทส่วนลด <span className="text-danger">*</span>{' '}
                        </Form.Label>
                        <Select
                          name="discount_type"
                          value={discountTypeOptions.find(option => option.value === values.discount_type)}
                          options={discountTypeOptions}
                          onChange={selectedOption => {
                            setFieldValue('discount_type', selectedOption?.value || '');

                            if (selectedOption?.value !== values.discount_type) {
                              setFieldValue('discount_value', '');
                            }
                          }}
                          placeholder="เลือกประเภทส่วนลด"
                          isDisabled={isSubmitting}
                          className="select-st-1"
                        />
                        {touched.discount_type && errors.discount_type && (
                          <div className="invalid-feedback d-block">{errors.discount_type}</div>
                        )}
                      </Form.Group>
                    </div>
                    <div className="col-6">
                      <Form.Group className="mb-3 form-st-3 pb-1">
                        <Form.Label className="mb-1">
                          จำนวน <span className="text-danger">*</span>{' '}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="discount_value"
                          className="form-control"
                          placeholder={'จำนวน'}
                          value={values.discount_value}
                          onChange={e => handleDiscountValueChange(e, setFieldValue, values.discount_type)}
                          isInvalid={touched.discount_value && !!errors.discount_value}
                          disabled={isSubmitting}
                          min="0"
                          max={values.discount_type === 'percent' ? '100' : undefined}
                          step="0.01"
                        />
                        <Form.Control.Feedback type="invalid">{errors.discount_value}</Form.Control.Feedback>
                      </Form.Group>
                    </div>
                  </div>

                  <div className="text-center pt-3 pb-3 btn-group-control">
                    <Button
                      type="button"
                      className="btn-st-6 me-3"
                      style={{ width: '100px' }}
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      ยกเลิก
                    </Button>
                    <Button type="submit" className="btn-st-3" style={{ width: '100px' }} disabled={isSubmitting}>
                      ยืนยัน
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </ModalAllder>
    </>
  );
}
